// React bindings of the sync engine: context, live queries shared between components, record and schema hooks.

import { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { accessOf, accountToday, bindMe, type Access, type Filter, type Id, type MetaIndex, type RecordObj, type SortKey } from '@confdeck/core-shared';
import type { SyncClient } from './client.ts';
import { LiveQuery } from './query.ts';
import type { StoreEvent } from './store.ts';

const Ctx = createContext<SyncClient | null>(null);

export function SyncProvider({ client, children }: { client: SyncClient; children: ReactNode }) {
  return <Ctx.Provider value={client}>{children}</Ctx.Provider>;
}

export function useSync(): SyncClient {
  const c = useContext(Ctx);
  if (!c) throw new Error('SyncProvider is missing');
  return c;
}

/** Re-renders when any event passes `pick`; returns a counter that changes on such events. */
function useStoreTick(pick: (e: StoreEvent) => boolean): number {
  const { store } = useSync();
  const tick = useRef(0);
  const pickRef = useRef(pick);
  pickRef.current = pick;
  return useSyncExternalStore(
    (cb) => store.subscribe((events) => { if (events.some((e) => pickRef.current(e))) { tick.current++; cb(); } }),
    () => tick.current,
  );
}

export function useMeta(): MetaIndex {
  const { store } = useSync();
  useStoreTick((e) => e.scope === 'meta');
  return store.meta;
}

/** Connection state and unsent count, for the status dot. */
export function useStatus() {
  const client = useSync();
  useStoreTick((e) => e.scope === 'status');
  return { state: client.state, unsent: client.unsent, presence: client.presence };
}

export function useRecord(id: Id | null | undefined): RecordObj | undefined {
  const { store } = useSync();
  useStoreTick((e) => !!id && ((e.scope === 'records' && (e.ids.includes(id) || !e.ids.length)) || (e.scope === 'links' && e.recordIds.includes(id))));
  return id ? store.record(id) : undefined;
}

/** Linked record ids of a relation field, re-rendering when links of this record change. */
export function useLinked(rec: RecordObj | undefined, fieldKey: string): Id[] {
  const { store } = useSync();
  useStoreTick((e) => !!rec && e.scope === 'links' && e.recordIds.includes(rec.id));
  const f = rec ? store.meta.field(rec.typeId, fieldKey) : undefined;
  return rec && f ? store.linked(rec, f) : [];
}

/** Re-renders when any record of the given types changes (titles in relation chips, counts). */
export function useTypesTick(typeIds: Id[]): number {
  return useStoreTick((e) => e.scope === 'records' && typeIds.includes(e.typeId));
}

type QueryEntry = { q: LiveQuery; refs: number; timer?: ReturnType<typeof setTimeout> };
/** Live queries per client: after a logout and another login a query never shows the previous person's store. */
const queriesByClient = new WeakMap<object, Map<string, QueryEntry>>();
const queriesOf = (client: object) => { let m = queriesByClient.get(client); if (!m) queriesByClient.set(client, (m = new Map())); return m; };

/**
 * A live query shared by key: the table and the board of one view use one result. Released queries
 * stay warm for 30 s, so switching back to a section is instant.
 */
export function useQuery(typeId: Id, raw: Filter | null, sort: SortKey[], search: string): LiveQuery {
  const client = useSync();
  // `$today` is the account's day (a view «Ближайшие»): a query made yesterday keeps its key until the day changes.
  const today = accountToday(client.store.meta);
  const filter = useMemo(() => bindMe(raw, client.header?.userId, today), [raw, client.header?.userId, today]);
  const key = useMemo(() => JSON.stringify([typeId, filter, sort, search]), [typeId, filter, sort, search]);
  const queries = queriesOf(client);
  const entry = useMemo(() => {
    let e = queries.get(key);
    if (!e) { e = { q: new LiveQuery(client.store, { typeId, filter, sort, search }), refs: 0 }; queries.set(key, e); }
    return e;
  }, [key, client]);
  useEffect(() => {
    entry.refs++;
    if (entry.timer) { clearTimeout(entry.timer); entry.timer = undefined; }
    return () => {
      entry.refs--;
      if (entry.refs === 0) entry.timer = setTimeout(() => { if (entry.refs === 0) { entry.q.dispose(); queries.delete(key); } }, 30_000);
    };
  }, [entry, key]);
  useSyncExternalStore((cb) => entry.q.subscribe(cb), () => entry.q.version);
  return entry.q;
}

/**
 * Warms the live query of a view, so opening the section later is instant (DESIGN.md «Скорость»: the
 * likely next screen is prepared in advance). The query stays in the shared cache like a released one.
 */
export function prefetchQuery(client: SyncClient, typeId: Id, filter: Filter | null, sort: SortKey[]) {
  filter = bindMe(filter, client.header?.userId, accountToday(client.store.meta));
  const key = JSON.stringify([typeId, filter, sort, '']);
  const queries = queriesOf(client);
  if (queries.has(key)) return;
  const e: QueryEntry = { q: new LiveQuery(client.store, { typeId, filter, sort, search: '' }), refs: 0 };
  queries.set(key, e);
  e.timer = setTimeout(() => { if (e.refs === 0) { e.q.dispose(); queries.delete(key); } }, 5 * 60_000);
}

/** Debounced value for search inputs. */
export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

/** The person's rights (decision 012), from the same calculation the server uses; the server still checks every write. */
export function useAccess(): Access {
  const client = useSync();
  const meta = useMeta();
  const user = meta.user(client.header?.userId);
  const scope = client.header?.scope?.typeId ?? null;
  // One calculation per schema, person and roles for all who ask: every row of a table asks, and each used to compute it anew.
  return useMemo(() => {
    const key = `${user?.id ?? ''}|${user?.rev ?? ''}|${scope ?? ''}`;
    const hit = accessCache.get(meta);
    if (hit?.key === key) return hit.access;
    const access = accessOf(meta, user, scope);
    accessCache.set(meta, { key, access });
    return access;
  }, [meta, user, user?.rev, scope]);
}
const accessCache = new WeakMap<object, { key: string; access: Access }>();
